import axios, { type AxiosInstance, AxiosError, type AxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiErrorData } from '../types/api';

// '/api' is proxied to the backend by Vite in dev and by nginx in the container:
// one frontend build for every environment.
const API_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  public instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_URL,
      timeout: 30000, // 30 second timeout to prevent hanging requests
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache', // Prevent caching issues
        'Pragma': 'no-cache',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        // A request the caller cancelled itself (the preview of « Répéter jusqu'à… » replaced by a
        // newer one) is not a failure: no error is logged, and it comes back as it is
        if (axios.isCancel(error)) return Promise.reject(error);

        // Handle timeout errors specifically
        if (error.code === 'ECONNABORTED') {
          console.error('Request timeout:', error.config?.url);
          return Promise.reject(new Error('La requête a expiré. Veuillez réessayer.'));
        }

        // Handle network errors
        if (error.code === 'ERR_NETWORK') {
          console.error('Network error:', error.config?.url);
          return Promise.reject(new Error('Erreur réseau. Vérifiez votre connexion.'));
        }

        if (error.response?.status === 401) {
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login') {
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: AxiosError): Error {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
    });

    if (error.response?.data) {
      const errorData = error.response.data as ApiErrorData;
      return new Error(errorData.detail || errorData.message || 'Une erreur est survenue');
    }
    if (error.request) {
      return new Error('Impossible de contacter le serveur');
    }
    return new Error('Erreur inattendue');
  }

  // Generic request method with retry logic
  private async makeRequest<T>(config: AxiosRequestConfig, retries = 2): Promise<T> {
    try {
      const response = await this.instance.request<T>(config);
      return response.data;
    } catch (error) {
      if (retries > 0 && error instanceof AxiosError && error.code === 'ECONNABORTED') {
        console.log(`Retrying request to ${config.url}, ${retries} attempts left`);
        return this.makeRequest(config, retries - 1);
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<ApiResponse<T>>({ ...config, method: 'GET', url: endpoint });
  }

  async post<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<ApiResponse<T>>({ ...config, method: 'POST', url: endpoint, data });
  }

  async put<T>(endpoint: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<ApiResponse<T>>({ ...config, method: 'PUT', url: endpoint, data });
  }

  async delete<T>(endpoint: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.makeRequest<ApiResponse<T>>({ ...config, method: 'DELETE', url: endpoint });
  }
}

export const apiClient = new ApiClient();